import React from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Field from '../Field';
import { mileageFrequencies, mileageLimits, mileageMessage as m, unitLabel } from '../../domain/mileage';
import { FREQUENCY_UNITS } from '../../domain/recurrence';
import s from './Mileage.module.css';
import { DateInput } from '../RegionInputs';

// A date input takes a five-digit year if digits are typed into a field that
// already holds one; min/max makes that unreachable, as on the expense drawer.
const DATE_MIN = '1900-01-01';
const DATE_MAX = '2999-12-31';

const title = (str) => str.charAt(0).toUpperCase() + str.slice(1);

// Add Mileage, as MMS draws it: a Details section of Date, Destination,
// Distance with the unit after it, Duration in Minutes, Vehicle, Odometer with
// the unit after it, and Notes — the last four captioned "Optional" and the
// first three not, which is how MMS marks what it requires. Then its
// Recurrence section, "One-time trip" / "Make this a recurring trip" (#519),
// which reveals Frequency, Repeats Every, Repeat indefinitely and Repeat
// Until (#554).
//
// Opened by "Repeat Trip" (#521), the fields it filled in are marked and
// the drawer says what the mark means, in MMS's words: "Indicates fields
// that were autofilled". `autofilled` is those fields, each until edited.
//
// The number boxes are text with a decimal keypad rather than
// `type="number"`: a decimal comma is a decimal point here, and a number
// input throws the comma — and the teacher's half mile — away before this
// code sees it.
export default function MileageDrawer({
  open, editing, form, errors = {}, canSave = true, saving, autofilled = [],
  onChange, onClose, onCancel, onSave, onSaveAndAdd,
}) {
  const limits = mileageLimits();
  const unit = unitLabel(form.unit);
  const err = (field) => (errors[field] ? 'true' : undefined);
  const filled = (field) => (autofilled.includes(field) ? 'true' : undefined);
  const recurring = form.recurrence === 'recurring';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={m(editing ? 'editTitle' : 'newTitle')}
      width={560}
      footer={(
        <>
          {editing ? null : (
            <Button variant="secondary" onClick={onSaveAndAdd} loading={saving === 'another'} disabled={!canSave}>
              {m('saveAndAdd')}
            </Button>
          )}
          <FooterSpacer />
          <Button variant="secondary" onClick={onCancel}>{m('cancel')}</Button>
          <Button onClick={onSave} loading={saving === 'save'} disabled={!canSave}>{m('save')}</Button>
        </>
      )}
    >
      <div className={s.body}>
        {autofilled.length ? (
          <p className={s.autofillNote}><span className={s.autofillMark} aria-hidden="true" />{m('autofilled')}</p>
        ) : null}
        <div className={s.sectionTitle}>{m('details')}</div>

        <Field label={m('fieldDate')} error={errors.occurredOn}>
          <DateInput value={form.occurredOn} min={DATE_MIN} max={DATE_MAX} data-error={err('occurredOn')}
            onChange={(e) => onChange({ occurredOn: e.target.value })}
          />
        </Field>

        <Field label={m('fieldDestination')} error={errors.destination}>
          <input
            value={form.destination} maxLength={limits.destinationMax} data-error={err('destination')}
            data-autofilled={filled('destination')}
            onChange={(e) => onChange({ destination: e.target.value })}
          />
        </Field>

        {/* `htmlFor`/`id` by hand: the unit sits in a wrapper beside the
            input, and `Field` labels only a child it can name (#383). */}
        <Field label={m('fieldDistance')} htmlFor="mileage-distance" error={errors.distance}>
          <span className={s.suffixed}>
            <input
              id="mileage-distance" inputMode="decimal" value={form.distance} data-error={err('distance')}
              data-autofilled={filled('distance')}
              onChange={(e) => onChange({ distance: e.target.value })}
            />
            <span className={s.suffix}>{unit}</span>
          </span>
        </Field>

        <Field label={m('fieldDuration')} htmlFor="mileage-duration" hint={m('optional')} error={errors.duration}>
          <span className={s.suffixed}>
            <input
              id="mileage-duration" inputMode="numeric" value={form.duration} data-error={err('duration')}
              data-autofilled={filled('duration')}
              onChange={(e) => onChange({ duration: e.target.value })}
            />
            <span className={s.suffix}>{m('minutes')}</span>
          </span>
        </Field>

        <Field label={m('fieldVehicle')} hint={m('optional')} error={errors.vehicle}>
          <input
            value={form.vehicle} maxLength={limits.vehicleMax} data-error={err('vehicle')}
            data-autofilled={filled('vehicle')}
            onChange={(e) => onChange({ vehicle: e.target.value })}
          />
        </Field>

        <Field label={m('fieldOdometer')} htmlFor="mileage-odometer" hint={m('optional')} error={errors.odometer}>
          <span className={s.suffixed}>
            <input
              id="mileage-odometer" inputMode="decimal" value={form.odometer} data-error={err('odometer')}
              onChange={(e) => onChange({ odometer: e.target.value })}
            />
            <span className={s.suffix}>{unit}</span>
          </span>
        </Field>

        <Field label={m('fieldNotes')} hint={m('optional')} error={errors.notes}>
          <textarea
            rows={3} value={form.notes} maxLength={limits.notesMax} data-error={err('notes')}
            onChange={(e) => onChange({ notes: e.target.value })}
          />
        </Field>

        <div className={s.divider}>
          <div className={s.sectionTitle}>{m('recurrence')}</div>
          {[['one_time', 'oneTime'], ['recurring', 'recurring']].map(([value, label]) => (
            <label key={value} className={s.checkRow}>
              <input
                type="radio" name="mileage-recurrence"
                checked={value === 'recurring' ? recurring : !recurring}
                onChange={() => onChange({ recurrence: value })}
              />
              <span>{m(label)}</span>
            </label>
          ))}

          {recurring ? (
            <>
              <div className={s.pair}>
                <Field label={m('frequency')}>
                  <select value={form.frequency} onChange={(e) => onChange({ frequency: e.target.value })}>
                    {mileageFrequencies().map((f) => <option key={f} value={f}>{title(f)}</option>)}
                  </select>
                </Field>
                {/* The unit follows the frequency, as on the expense drawer. */}
                <Field label={m('repeatsEvery')} htmlFor="mileage-interval" error={errors.interval}>
                  <span className={s.suffixed}>
                    <input
                      id="mileage-interval" inputMode="numeric" maxLength={2} value={form.interval}
                      data-error={err('interval')}
                      onChange={(e) => onChange({ interval: e.target.value })}
                    />
                    <span className={s.suffix}>{FREQUENCY_UNITS[form.frequency] || ''}</span>
                  </span>
                </Field>
              </div>

              <label className={s.checkRow}>
                <input
                  type="checkbox" checked={form.repeatIndefinitely}
                  onChange={(e) => onChange({ repeatIndefinitely: e.target.checked })}
                />
                <span>{m('repeatIndefinitely')}</span>
              </label>

              {!form.repeatIndefinitely ? (
                <div className={s.narrow}>
                  <Field label={m('repeatUntil')} error={errors.endDate}>
                    <input
                      type="date" value={form.endDate} min={form.occurredOn || DATE_MIN} max={DATE_MAX}
                      data-error={err('endDate')}
                      onChange={(e) => onChange({ endDate: e.target.value })}
                    />
                  </Field>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}
